<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import { PropertyPhotoCarouselDesktop } from '~/components/PropertyPhotoCarouselDesktop';
import { PropertyPhotoCarouselMobile } from '~/components/PropertyPhotoCarouselMobile';

// Note: These carousels are critical for LCP and use CSS-based visibility (hidden/xl:block, xl:hidden)
// Lazy hydration with media queries causes hydration mismatches because both are SSR-rendered
// but the wrapper elements interfere with DOM matching during hydration

interface PropertyHeroCarouselsProps {
	property: Property | null;
	isLoading: boolean;
	shouldShowFeaturedVideo: boolean;
}

interface PropertyHeroCarouselsEmits {
	'photo-clicked': [
		payload: {
			photoIndex: number;
			photoUrl: string;
			clickType: 'photo' | 'cta';
		}
	];
	'video-clicked': [videoUrl?: string];
}

const props = defineProps<PropertyHeroCarouselsProps>();
defineEmits<PropertyHeroCarouselsEmits>();

function normalizeVideoDimension(value: unknown): number | undefined {
	const normalizedValue = Number(value);

	return Number.isFinite(normalizedValue) && normalizedValue > 0
		? normalizedValue
		: undefined;
}

const featuredVideo = computed(() =>
	props.shouldShowFeaturedVideo
		? props.property?.featuredVideos?.[0]
		: undefined
);

const featuredVideoUrl = computed(() => featuredVideo.value?.url);
const featuredVideoPosterUrl = computed(() => featuredVideo.value?.thumbnail);

// Both carousels are always in the DOM (CSS visibility) for LCP optimization.
// Only pass the video URL to the visible one to avoid two Shaka players
// loading the same segments simultaneously.
const breakpoints = import.meta.client
	? useBreakpoints(breakpointsTailwind)
	: null;
const isDesktop = computed(
	() => breakpoints?.greaterOrEqual('xl').value ?? false
);
const desktopVideoUrl = computed(() =>
	isDesktop.value ? featuredVideoUrl.value : undefined
);
const mobileVideoUrl = computed(() =>
	!isDesktop.value ? featuredVideoUrl.value : undefined
);
const featuredVideoWidth = computed(() =>
	normalizeVideoDimension(featuredVideo.value?.width)
);
const featuredVideoHeight = computed(() =>
	normalizeVideoDimension(featuredVideo.value?.height)
);
</script>

<template>
	<!-- Photo Carousels - both rendered with CSS-based visibility for LCP optimization -->
	<PropertyPhotoCarouselDesktop
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="desktopVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
		@video-clicked="$emit('video-clicked', $event)"
	/>

	<PropertyPhotoCarouselMobile
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="mobileVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
		@video-clicked="$emit('video-clicked', $event)"
	/>
</template>
